// src/pages/admin/AdminDashboard.jsx
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useAllUsers,
  useAllTodayAttendance,
  useAnnouncements,
  useAllLeaveRequests,
  useUpcomingMeetings,
} from "../../hooks/useFirestore";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import AttendanceCard from "../../components/attendance/AttendanceCard";
import { getTodaysMeetings, formatTimeStr } from "../../utils/meetings";
import { useUpcomingHolidays, useHolidays } from "../../hooks/useFirestoreExtensions";
import { isPresent } from "../../utils/attendanceStatus";
import { calculateDailyStatsForEmployees } from "../../utils/statusCalculations";

/** Returns today's date as "YYYY-MM-DD" in local time. */
function getTodayStr() {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Counts how many approved leave requests cover today's date.
 * A leave request covers today if: fromDate <= today <= toDate
 */
function countOnLeaveToday(leaveRequests) {
  const today = getTodayStr();

  return leaveRequests.filter(
    (r) =>
      r.status === "approved" &&
      r.fromDate <= today &&
      r.toDate >= today,
  ).length;
}

export default function AdminDashboard() {
  const { userProfile } = useAuth();

  const { data: users, loading: uLoad } = useAllUsers();
  const { data: attendance, loading: aLoad } = useAllTodayAttendance();
  const { data: meetings, loading: mLoad } = useUpcomingMeetings();
  const { data: announcements } = useAnnouncements();
  const { data: leaveRequests } = useAllLeaveRequests();
  const { data: upcomingHolidays } = useUpcomingHolidays();
  const { data: holidays } = useHolidays();

  // ── Stats Calculation ───────────────────────
  const todayStrISO = getTodayStr();
  const employees = users.filter((u) => u.isActive);

  const present = attendance.filter(isPresent);

  const todayHoliday = holidays.find((h) => h.date === todayStrISO);
  const isHolidayToday = !!todayHoliday;

  const dailyStats = calculateDailyStatsForEmployees({
    employees,
    attendanceRecords: attendance,
    leaveRequests,
    isHolidayToday,
    dateStr: todayStrISO,
  });

  const absent = dailyStats.absent;
  const onLeaveCount = dailyStats.leaves;
  const attendRate = dailyStats.rate;

  // Intern stats (covers paid-intern, unpaid-intern, and legacy intern)
  const INTERN_TYPES = ["paid-intern", "unpaid-intern", "intern"];
  const interns = users.filter(
    (u) => INTERN_TYPES.includes(u.employmentType) && u.isActive,
  );

  const completingSoon = interns.filter((u) => {
    if (!u.internshipEndDate) return false;

    const days = Math.ceil(
      (new Date(u.internshipEndDate) - new Date()) / 86400000,
    );

    return days > 0 && days <= 30;
  });


  // const todayMeetings = meetings
  //   .filter(
  //     (m) =>
  //       m.date === todayStrISO &&
  //       new Date(`${m.date}T${m.endTime || m.time}`) >= new Date(),
  //   )
  //   .sort((a, b) => a.time.localeCompare(b.time));
  // // useMeeting returns today's active (not-yet-expired) meetings.
  // // Filter by visibility rules to keep it completely consistent.
  const todayMeetings = getTodaysMeetings(meetings, userProfile);

  const todayStr = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const uid = userProfile?.uid || userProfile?.id;

  // ── KPI Popup State ───────────────────────
  const [popupTitle, setPopupTitle] = useState("");
  const [popupData, setPopupData] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  // Present employee ids
  const presentIds = present.map((p) => p.userId);

  // Employees currently on leave
  const employeesOnLeave = employees.filter((emp) =>
    leaveRequests.some(
      (lr) =>
        lr.userId === emp.uid &&
        lr.status === "approved" &&
        lr.fromDate <= todayStrISO &&
        lr.toDate >= todayStrISO,
    ),
  );

  // Absent employees
  const absentEmployees = employees.filter(
    (emp) =>
      !presentIds.includes(emp.uid) &&
      !employeesOnLeave.some((e) => e.uid === emp.uid),
  );

  // Present employees full data
  const presentEmployees = employees.filter((emp) =>
    presentIds.includes(emp.uid),
  );

  // Open popup helper
  function openPopup(title, data) {
    setPopupTitle(title);
    setPopupData(data);
    setShowPopup(true);
  }

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{todayStr}</p>
        </div>

        <div className="user-greeting">
          <span>
            Welcome, {userProfile?.displayName?.split(" ")[0]}
          </span>
        </div>
      </div>

      {/* ── KPI Cards ─────────────────────────── */}
      <div className="kpi-grid">
        {/* Total Employees */}
        <KpiCard
          label="Total Employees"
          value={uLoad ? "—" : employees.length}
          sub="Active headcount"
          onClick={() => openPopup("Total Employees", employees)}
          icon={
            <div
              style={{
                width: "45px",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                style={{
                  width: "32px",
                  height: "32px",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                />
              </svg>
            </div>
          }
          color="blue"
        />

        {/* Present */}
        <KpiCard
          label="Present Today"
          value={aLoad ? "—" : present.length}
          sub={`${attendRate}% attendance`}
          onClick={() =>
            openPopup("Present Employees", presentEmployees)
          }
          icon={
            <div
              style={{
                width: "45px",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                style={{
                  width: "30px",
                  height: "30px",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m4.5 12.75 6 6 9-13.5"
                />
              </svg>
            </div>
          }
          color="green"
        />

        {/* Absent */}
        <KpiCard
          label="Absent Today"
          value={aLoad ? "—" : absent}
          sub={`${employees.length > 0
              ? Math.round((absent / employees.length) * 100)
              : 0
            }% absence`}
          onClick={() =>
            openPopup("Absent Employees", absentEmployees)
          }
          icon={
            <div
              style={{
                width: "45px",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                style={{
                  width: "30px",
                  height: "30px",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </div>
          }
          color="red"
        />

        {/* Leave */}
        <KpiCard
          label="On Leave Today"
          value={aLoad ? "—" : onLeaveCount}
          sub="Approved leaves"
          onClick={() =>
            openPopup("Employees On Leave", employeesOnLeave)
          }
          icon={
            <div
              style={{
                width: "45px",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                style={{
                  width: "30px",
                  height: "30px",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                />
              </svg>
            </div>
          }
          color="amber"
        />

        {/* Interns */}
        <KpiCard
          label="Active Interns"
          value={uLoad ? "—" : interns.length}
          sub={
            completingSoon.length > 0
              ? `${completingSoon.length} completing soon`
              : "All on track"
          }
          onClick={() =>
            openPopup("Active Interns", interns)
          }
          icon={
            <div
              style={{
                width: "45px",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                style={{
                  width: "30px",
                  height: "30px",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                />
              </svg>
            </div>
          }
          color="purple"
        />
      </div>

      {/* ── Bottom Grid ─────────────────────── */}
      <div className="dashboard-grid">
        {/* Attendance */}
        <AttendanceCard
          userId={uid}
          userProfile={userProfile}
        />

        {/* Employee Status */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">
              Employee Status
            </h3>

            <span className="card-badge">
              {users.filter((u) => u.isActive).length} active
            </span>
          </div>

          {uLoad ? (
            <div className="card-loading">Loading…</div>
          ) : users.length === 0 ? (
            <div className="card-empty">
              No employees found.
              No punch-ins yet today.
            </div>
          ) : (
            <div className="attendance-list" style={{ maxHeight: "400px", overflowY: "auto", paddingRight: "0.5rem" }}>
              {users.map((u) => (
                <div
                  key={u.uid || u.id}
                  className="attendance-row"
                >
                  <div className="attendance-avatar">
                    {u.displayName?.slice(0, 2).toUpperCase()}
                  </div>

                  <div className="attendance-info">
                    <span className="attendance-name">
                      {u.displayName}
                    </span>

                    <span className="attendance-dept">
                      {u.department}
                    </span>
                  </div>

                  <div className="attendance-time">
                    <span className={`status-pill status-pill--${u.isActive ? "green" : "red"}`}>
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Meetings */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">
              Today's Meetings
            </h3>

            <span className="card-badge">
              {todayMeetings.length}
            </span>
          </div>

          {mLoad ? (
            <div className="card-loading">Loading…</div>
          ) : todayMeetings.length === 0 ? (
            <div className="card-empty">
              No more meetings today.
            </div>
          ) : (
            <div className="meetings-list">
              {todayMeetings.slice(0, 5).map((m) => (
                <div key={m.id} className="meeting-row">
                  <div className="meeting-dot" />

                  <div className="meeting-info">
                    <span className="meeting-title">
                      {m.title}
                    </span>

                    <span className="meeting-time">
                      {m.date} · {formatTimeStr(m.startTime || m.time)}{m.endTime && ` - ${formatTimeStr(m.endTime)}`}
                    </span>
                  </div>

                  {m.meetLink && (
                    <a
                      href={m.meetLink}
                      target="_blank"
                      rel="noreferrer"
                      className="meeting-join-btn"
                    >
                      Join
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Announcements */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">
              Recent Announcements
            </h3>
          </div>

          {announcements.length === 0 ? (
            <div className="card-empty">
              No announcements yet.
            </div>
          ) : (
            <div className="announcements-list">
              {announcements.slice(0, 4).map((a) => (
                <div
                  key={a.id}
                  className={`announcement-row priority-${a.priority}`}
                >
                  <span className="announcement-priority-dot" />

                  <div className="announcement-content">
                    <span className="announcement-title">
                      {a.title}
                    </span>

                    <span className="announcement-msg">
                      {a.message}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Department Breakdown */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">
              Department Breakdown
            </h3>
          </div>

          <DeptBreakdown users={employees} />
        </div>

        {/* Upcoming Holidays */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Upcoming Holidays</h3>
            <span className="card-badge">{upcomingHolidays.length}</span>
          </div>
          {upcomingHolidays.length === 0 ? (
            <div className="card-empty">No upcoming holidays scheduled.</div>
          ) : (
            <div className="announcements-list">
              {upcomingHolidays.slice(0, 5).map((h) => {
                const dt = new Date(h.date + "T12:00:00");
                const daysLeft = Math.ceil((dt - new Date()) / 86400000);
                const typeColor = h.type === "public" ? "green" : h.type === "optional" ? "blue" : "amber";
                return (
                  <div key={h.id} className="announcement-row">
                    <span className="announcement-priority-dot" />
                    <div className="announcement-content">
                      <span className="announcement-title">🎉 {h.name}</span>
                      <span className="announcement-msg">
                        {dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {" · "}
                        <span className={`status-pill status-pill--${typeColor}`} style={{ fontSize: "0.65rem" }}>{h.type}</span>
                        {" · "}
                        {daysLeft === 1 ? "Tomorrow!" : `in ${daysLeft} days`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── KPI Popup Modal ─────────────────── */}
      {showPopup && (
        <div
          className="kpi-modal-overlay"
          onClick={() => setShowPopup(false)}
        >
          <div
            className="kpi-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="kpi-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                <h2 className="kpi-modal-title">
                  {popupTitle}
                </h2>
                <span className="kpi-modal-badge">{popupData.length}</span>
              </div>

              <button
                className="kpi-modal-close"
                onClick={() => setShowPopup(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Employee List */}
            {popupData.length === 0 ? (
              <div className="card-empty">
                No employees found.
              </div>
            ) : (
              <div className="kpi-modal-list">
                {popupData.map((emp) => (
                  <div
                    key={emp.uid}
                    className="kpi-modal-item"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.85rem",
                      }}
                    >
                      <div className="kpi-modal-avatar">
                        {emp.displayName
                          ?.slice(0, 2)
                          .toUpperCase()}
                      </div>

                      <div>
                        <div className="kpi-modal-name">
                          {emp.displayName}
                        </div>

                        <div className="kpi-modal-dept">
                          {emp.department ||
                            "No Department"}
                        </div>
                      </div>
                    </div>

                    <div className="kpi-modal-tag">
                      {emp.employeeId || "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

// ── Sub Components ───────────────────────────

function KpiCard({
  label,
  value,
  sub,
  icon,
  color,
  onClick,
}) {
  return (
    <div
      className={`kpi-card kpi-card--${color}`}
      onClick={onClick}
      style={{
        cursor: "pointer",
        transition: "0.2s ease",
      }}
    >
      <div className="kpi-icon">{icon}</div>

      <div className="kpi-body">
        <span className="kpi-value">{value}</span>

        <span className="kpi-label">{label}</span>

        <span className="kpi-sub">{sub}</span>
      </div>
    </div>
  );
}


function DeptBreakdown({ users }) {
  const depts = {};

  users.forEach((u) => {
    depts[u.department] =
      (depts[u.department] || 0) + 1;
  });

  const sorted = Object.entries(depts).sort(
    (a, b) => b[1] - a[1],
  );

  const max = sorted[0]?.[1] || 1;

  if (sorted.length === 0) {
    return <div className="card-empty">No data.</div>;
  }

  return (
    <div className="dept-breakdown">
      {sorted.map(([dept, count]) => (
        <div key={dept} className="dept-row">
          <span className="dept-name">{dept}</span>

          <div className="dept-bar-wrap">
            <div
              className="dept-bar"
              style={{
                width: `${(count / max) * 100}%`,
              }}
            />
          </div>

          <span className="dept-count">{count}</span>
        </div>
      ))}
    </div>
  );
}